import {
  MaritalStatus,
  ResidencyStatus,
  TaxpayerType,
} from '../../pages/BtlApplicationFormPage/Applicants/Individual/types';
import { Valuation } from './valuationTypes';

export enum RefinancePurpose {
  CAPITAL_RAISING = 'CAPITAL_RAISING',
  LIKE_FOR_LIKE = 'LIKE_FOR_LIKE',
}

export enum DepositSourceType {
  PERSONAL_SAVINGS = 'PERSONAL_SAVINGS',
  GIFT = 'GIFT',
  GIFTED_EQUITY = 'GIFTED_EQUITY',
  SALE_OF_EXISTING_PROPERTY = 'SALE_OF_EXISTING_PROPERTY',
  REMORTGAGE_OF_EXISTING_PROPERTY = 'REMORTGAGE_OF_EXISTING_PROPERTY',
  INHERITANCE = 'INHERITANCE',
  OTHER = 'OTHER',
  UNKNOWN = 'UNKNOWN',
}

export type DepositSource = {
  id: string;
  sourceType: DepositSourceType;
  amount: number;
  description: string | null;
};

export interface UpsertDepositSourceDto {
  id?: string;
  sourceType: DepositSourceType;
  amount: number;
  description?: string | null;
}

export type BtlApplication = {
  id: string;
  title: string;
  isTopSlicing: boolean;
  submittedAt: Date | null;
  referenceNumber: string;
  status: ApplicationStatus;
  applicationType: BtlApplicationType;
  purpose: BtlPurpose;
  refinancePurpose: RefinancePurpose | null;
  termInMonths: number;
  depositAmount: number | null;
  depositSources?: DepositSource[];
  financingAmount: number;
  grossFinancingAmount: number;
  ftv: number;
  paymentProfile: PaymentProfile;
  feesAddedToBalance: boolean;
  owner: any | null; // TODO: Needs the User type
  ownerName: string;
  product?: any | null; // TODO: Needs the Product type
  borrower: Borrower;
  isExpatriate: boolean;
  isFirstTimeBuyer: boolean;
  isHmoOrMub: boolean;
  isPortfolioLandlord: boolean;
  properties: ApplicationProperty[];
  applicationProperty: ApplicationProperty;
  property: BtlProperty;
  submissionRoute: SubmissionRoute;
  outstandingBalance: number | null;
  privacyAccepted: boolean | null;
  termsConfirmed: boolean | null;
  creditConsentAccepted: boolean | null;
  preSubmitWarnings: string[];
  isTopSlicingRestricted: boolean;
  uiState: CaseUiState | null;
  isCurrentProductSuitable: boolean;
  assertions: {
    assertions: Assertion[];
  };
  customerSolicitor?: {
    solicitorFirm: SolicitorFirm;
    solicitor?: Solicitor;
  };
  numberOfIndividuals: number;
  netFinancingFtv: number;
  isAffordabilityRunRequired: boolean;
};

export enum ApplicationStatus {
  NEW = 'NEW',
  DIP_SUBMITTED = 'DIP_SUBMITTED',
  APPLICATION_SUBMITTED = 'APPLICATION_SUBMITTED',
  REVIEWING_DIP = 'REVIEWING_DIP',
  DIP_REFERRED = 'DIP_REFERRED',
  DIP_DECLINED = 'DIP_DECLINED',
  DIP_EXPIRED = 'DIP_EXPIRED',
  DIP_ACCEPTED = 'DIP_ACCEPTED',
  AWAITING_PROPERTY = 'AWAITING_PROPERTY',
  PROPERTY_SUBMITTED = 'PROPERTY_SUBMITTED',
  BOOKING_PRE_ADVICE = 'BOOKING_PRE_ADVICE',
  PRE_ADVICE_BOOKED = 'PRE_ADVICE_BOOKED',
  ADVICE_BOOKED = 'ADVICE_BOOKED',
  PRODUCT_NOT_SUITABLE = 'PRODUCT_NOT_SUITABLE',
  RECOMMENDED = 'RECOMMENDED',
  FIS_ISSUED = 'FIS_ISSUED',
  PAYMENT_DUE = 'PAYMENT_DUE',
  BTL_APPLICATION_SUBMITTED = 'BTL_APPLICATION_SUBMITTED',
  FEES_PAID = 'FEES_PAID',
  REVIEWING_APPLICATION = 'REVIEWING_APPLICATION',
  PROPERTY_DECLINED = 'PROPERTY_DECLINED',
  APPLICATION_DECLINED = 'APPLICATION_DECLINED',
  OFFER_ISSUED = 'OFFER_ISSUED',
  OFFER_ACCEPTED = 'OFFER_ACCEPTED',
  OFFER_EXPIRED = 'OFFER_EXPIRED',
  OFFER_BEING_REVISED = 'OFFER_BEING_REVISED',
  COMPLETION = 'COMPLETION',
}

export type CaseUiState = {
  topSlicingEnabled?: boolean;
};

export type IntermediaryFee = {
  id: string;
  description: string;
  amount: number;
  whenPayable: string;
  howPayable: string;
  refundableAmount: number;
};

export interface UpsertIntermediaryFeeDto {
  id?: string;
  description: string;
  amount: number;
  whenPayable: string;
  howPayable: string;
  refundableAmount: number;
}

export type SubmissionRoute = {
  id: string;
  channel: Channel;
  submissionType: SubmissionType;
  referrerId: string | undefined;
  submissionFunnelType?: 'DIRECT' | 'MORTGAGE_CLUB' | 'NETWORK';
  intermediaryFees?: IntermediaryFee[];
};

export type CreateReferrerDto = {
  firstName: string;
  lastName: string;
  emailAddress?: string;
  phoneNumber?: string;
};

export type Channel = 'DIRECT' | 'REFERRED';

export type ApplicationProperty = {
  tenure: PropertyTenure;
  yearsRemainingOnLease: number | null;
  status: PropertyStatus;
  property: BtlProperty;
  estateAgent: UpdateEstateAgent;
};

export enum PropertyTenure {
  'FREEHOLD' = 'FREEHOLD',
  'LEASEHOLD' = 'LEASEHOLD',
}

export type PropertyStatus = 'ACTIVE' | 'REJECTED' | 'EXPECTED';

export type BtlProperty = {
  id: string;
  purchasePrice?: number;
  estimatedMarketValue?: number;
  propertyType: PropertyType;
  propertySubType: PropertySubType | null;
  expectedPropertyType: ExpectedPropertyType | null;
  grossMonthlyRentalPayment: number;
  region?: ResidenceRegion | null;
  address: BtlAddress | null;
  attributes: PropertyAttributes | null;
  valuation?: Valuation;
};

export type PropertyAttributes = {
  // only the subset are declared here that are currently collected for the application
  numberOfBedrooms: number | null;
  hasCommunalSpace: boolean | null;
  numberOfUnits: number | null;
};

export type Borrower = {
  id: string;
  salutation: string;
  customSalutation: string | null;
  individuals: Individual[];
  companies: BtlCompany[];
  bankAccount: BankAccount;
  borrowerType: BorrowerType;
  individualOrder: string[];
};

export type BorrowerType = 'INDIVIDUAL' | 'COMPANY';

export type BtlCompany = {
  id: string;
  companyName: string;
  companyNumber: string;
  registeredAddress: BtlAddress;
};

export type BtlApplicationType = 'OWNER_OCCUPIED' | 'BUY_TO_LET';

export type CreateCaseDto = {
  purpose: BtlPurpose;
  termInMonths: number;
  depositAmount?: number | null;
  financingAmount: number;
  paymentProfile: PaymentProfile;
  repaymentStrategy?: RepaymentStrategy | null;
  repaymentStrategyDetails?: string | null;
  feesAddedToBalance: boolean;
  property: CreateCaseProperty;
  submissionType: SubmissionType;
  submissionFunnelType?: 'DIRECT' | 'MORTGAGE_CLUB' | 'NETWORK';
  introducer?: CreateReferrerDto;
  applicationType: BtlApplicationType;
  intermediaryFees?: UpsertIntermediaryFeeDto[];
};

export type UpdateCaseDto = {
  caseId: string;
  propertyId?: string;
  financeData: UpdateCaseFinance;
  propertyData: UpdateApplicationProperty;
  submissionFunnelType?: 'DIRECT' | 'MORTGAGE_CLUB' | 'NETWORK';
  introducer?: CreateReferrerDto;
  intermediaryFees?: UpsertIntermediaryFeeDto[];
};

export type UpdateCaseFinance = {
  purpose: BtlPurpose;
  feesAddedToBalance: boolean;
  productId?: number | null;
  termInMonths: number;
  depositAmount: number | null;
  depositSources?: UpsertDepositSourceDto[];
  financingAmount: number;
  outstandingBalance: number | null;
  paymentProfile: PaymentProfile;
  repaymentStrategy?: RepaymentStrategy | null;
  repaymentStrategyDetails?: string | null;
};

export type UpdateApplicationProperty = {
  tenure: PropertyTenure;
  yearsRemainingOnLease: number | null;
  property: UpdateBtlPropertyDto;
  estateAgent?: UpdateEstateAgent;
};

export type UpdateEstateAgent = {
  name: string;
  agency: string;
  emailAddress: string;
  phoneNumber: string;
};

export type UpdateBtlPropertyDto = {
  purchasePrice: number;
  propertyType: PropertyType | null;
  propertySubType: PropertySubType | null;
  expectedPropertyType: ExpectedPropertyType | null;
  grossMonthlyRentalPayment: number;
  address: Address;
  region?: ResidenceRegion | null;
};

export type BtlPurpose = 'PURCHASE' | 'REFINANCE';
export type PaymentProfile = 'RENT_ONLY' | 'ACQUISITION';
export type RepaymentStrategy =
  | 'SALE_OF_INVESTMENT_PROPERTY'
  | 'ENDOWMENT_POLICY'
  | 'UK_PENSIONS'
  | 'REGULAR_SAVINGS_AND_INVESTMENTS'
  | 'STOCKS_AND_SHARES_ISA'
  | 'OTHER';
export type SubmissionType = 'INTRODUCED' | 'ADVISED';

export type BtlCaseSubmissionResult = {
  success: boolean;
  warnings: string[];
  affordabilityResponse?: BtlAffordabilityRunResponse;
};

export type AffordabilityRunOutput = {
  id: string;
  modelVersion: string;
  isPassed: boolean;
  monthlyPayment: number;
  totalDisposableIncome: number | null;
  isTopSlicingApplied: boolean;
  isEligibleForTopSlicing: boolean;
  rcr: number | null;
  applicationLtv: number;
  minRCRThreshold: number;
  minRentalIncome: number;
  maxFinancingPossible: number;
  maxFinancingImpliedByProductFTV: number;
  maxFinancingImpliedByUpperLimit: number;
  isNetFinancingAmountAboveMinThreshold: boolean;
  isNetFinancingAmountBelowMaxThreshold: boolean;
  isPropertyValueAboveMinThreshold: boolean;
  isRentalIncomeAboveMinThreshold: boolean;
  isProductFtvAboveOrEqualToApplicationFtv: boolean;
  isDisposableIncomeOverMinimum: boolean | null;
};

export type OwnerOccupiedAffordabilityRunOutput = {
  id: string;
  financingAmount: number;
  isPassed: boolean;
  maximumFinancingPossible: number;
  ftiMultiple: number | null;
  applicant1GrossIncome: number;
  applicant2GrossIncome: number;
  applicant3GrossIncome: number;
  applicant4GrossIncome: number;
  netDisposableIncomeOnSpotDate: number;
  netDisposableIncomeOnRevertDate: number;
  onsExpenditure: number;
  initialMonthlyPayment: number;
  stressedMonthlyPaymentOnSpotDate: number;
  stressedMonthlyPaymentOnRevertDate: number;
  initialResidual: number;
  stressResidualOnSpotDate: number;
  stressResidualOnRevertDate: number;
  equityShare: number;
  buyoutShare: number;
  exception: string;
  usedDeclaredExpenditure: number;
  passedUsingDeclaredExpenditureOrONS: string;
  maxFinancingImpliedByAffordability: number;
  maxFinancingImpliedByProductFTV: number;
  maxFinancingImpliedByFTIMultiple: number;
  maxFinancingImpliedByUpperLimit: number;
  minFinancingImpliedByLowerLimit: number;
  productName: string;
  modelVersion: string;
};

export type AffordabilityRunRecord = {
  createdAt?: string | Date;
  buyToLetOutput?: AffordabilityRunOutput;
  ownerOccupiedOutput?: OwnerOccupiedAffordabilityRunOutput;
};

export interface BtlAffordabilityRunResponse {
  isPassed: boolean;
  maxFinancingPossible: {
    value: number;
    breakdown: {
      maxFinancingImpliedByProductFTV: number;
      maxFinancingImpliedByUpperLimit: number;
    };
  };
  isEligibleForTopSlicing: boolean;
  isTopSlicingApplied: boolean;
  checks: {
    isNetFinancingAmountAboveMinThreshold: boolean;
    isNetFinancingAmountBelowMaxThreshold: boolean;
    isPropertyValueAboveMinThreshold: boolean;
    isRentalIncomeAboveMinThreshold: boolean;
    isProductFtvAboveOrEqualToApplicationFtv: boolean;
    isDisposableIncomeOverMinimum: boolean | null;
    isAllPassed: boolean;
  };
  financing: {
    totalDisposableIncome: number | null;
    monthlyPayment: number;
    households:
      | {
          monthlyPostTaxIncome: number;
          disposableIncome: number;
          expenditure: number;
          creditCommitments: number;
          creditCardAssumedCost: number;
        }[]
      | null;
  };
  factors: {
    applicationLtv: number;
    minRCRThreshold: number;
    minRentalIncome: number;
    rcr: number;
  };
  metadata: {
    modelVersion: string;
    timestamp: string;
  };
}

type CreateCaseProperty = {
  purchasePrice: number;
  propertyType: PropertyType;
  propertySubType?: PropertySubType;
  grossMonthlyRentalPayment: number;
  propertyAddress?: Address;
};

export enum ExpectedPropertyType {
  STANDARD = 'STANDARD',
  HMO_OR_MUB = 'HMO_OR_MUB',
}

export enum PropertyType {
  HOUSE = 'HOUSE',
  FLAT = 'FLAT',
  MAISONETTE = 'MAISONETTE',
  BUNGALOW = 'BUNGALOW',
  TERRACED = 'TERRACED',
  HMO = 'HMO',
  MUB = 'MUB',
  OTHER = 'OTHER',
}

export enum PropertySubType {
  PURPOSE_BUILT = 'PURPOSE_BUILT',
  CONVERTED = 'CONVERTED',
  SEMI_DETACHED = 'SEMI_DETACHED',
  DETACHED = 'DETACHED',
  TERRACED = 'TERRACED',
  END_OF_TERRACE = 'END_OF_TERRACE',
}

type Address = {
  addressLine1: string;
  addressLine2?: string;
  postcode: string;
  town: string;
  country: string;
};

export type Individual = {
  id: string;
  title: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  dateOfBirth: Date;
  maritalStatus: MaritalStatus;
  nationality: string;
  retiringWithin10Years: boolean | null;
  projectedRetirementPayeIncome: number | null;
  previousTitle: string | null;
  previousFirstName: string | null;
  previousMiddleName: string | null;
  previousLastName: string | null;
  countryOfResidence: string;
  isExpatriate: boolean | null;
  isPortfolioLandlord: boolean | null;
  isFirstTimeBuyer: boolean | null;
  isFirstTimeLandlord: boolean | null;
  yearsLandlordExperience: number | null;
  relationshipToSpv: RelationshipToCompany | null;
  bankruptcy: boolean | null;
  ccj: boolean | null;
  creditDefaults: boolean | null;
  paydayLoans: boolean | null;
  propertyRepossession: boolean | null;
  criminalConvictions: boolean | null;
  missedFinancePayments: boolean | null;
  isPoliticallyExposedPerson: boolean | null;
  taxpayerType: TaxpayerType | null;
  isPrimary: boolean | null;
  residencyStatus: UkResidency;
  incomes: IndividualIncome[];
  contactDetails: ContactDetails[];
  addressHistory: AddressHistory[];
  expectedGrossIncome: number | null;
  isVulnerable: boolean | null;
  vulnerabilityCategory: VulnerabilityCategory | null;
  vulnerabilityDetails: string | null;
  vulnerabilityDateIdentified: Date | null;
};

export enum RelationshipToPrimaryApplicant {
  BUSINESS_PARTNER = 'BUSINESS_PARTNER',
  MARRIED = 'MARRIED',
  CIVIL_PARTNERSHIP = 'CIVIL_PARTNERSHIP',
  PARTNER_NOT_MARRIED = 'PARTNER_NOT_MARRIED',
  SIBLING = 'SIBLING',
  FRIEND = 'FRIEND',
  PARENT = 'PARENT',
  CHILD = 'CHILD',
  UNKNOWN = 'UNKNOWN',
}

export enum VulnerabilityCategory {
  LIFE_EVENT = 'LIFE_EVENT',
  HEALTH = 'HEALTH',
  RESILIENCE = 'RESILIENCE',
  CAPABILITIES = 'CAPABILITIES',
  OTHER = 'OTHER',
}

export enum RelationshipToCompany {
  DIRECTOR = 'DIRECTOR',
  SHAREHOLDER = 'SHAREHOLDER',
}

export type UkResidency = {
  id: string;
  status: ResidencyStatus;
  visaEndDate: Date | null;
  dualNationality: string | null;
};

// TODO: what becomes of this!??
export type IndividualIncome = {
  id: string;
  isPrimary: boolean;
  companyName: string | null;
  occupation: string | null;
  payeIncome: PayeIncome | null;
  salariedDirectorIncome: SalariedDirectorIncome | null;
  soleTraderIncome: SoleTraderIncome | null;
  contractorIncome: ContractorIncome | null;
  partnershipIncome: PartnershipIncome | null;
  genericIncome: GenericIncome | null;
};

export type PayeIncome = {
  id: string;
  timeInJob: number;
  basicIncome: number;
  employersAllowance: number;
  bonus: number;
  commission: number;
  overtime: number;
};

export type SalariedDirectorIncome = {
  id: string;
  timeTrading: number;
  salaryMostRecentYear: number;
  salaryPriorYear: number;
  dividendsMostRecentYear: number;
  dividendsPriorYear: number;
};

export type SoleTraderIncome = {
  id: string;
  timeTrading: number;
  netTaxableIncomeMostRecentYear: number;
  netTaxableIncomePriorYear: number;
};

export type ContractorIncome = {
  id: string;
  timeTrading: number;
  timeRemainingOnContract: number;
  netTaxableIncomeMostRecentYear: number;
  netTaxableIncomePriorYear: number;
};

export type PartnershipIncome = {
  id: string;
  timeTrading: number;
  netTaxableIncomeMostRecentYear: number;
  netTaxableIncomePriorYear: number;
};

export type GenericIncome = {
  id: string;
  type: GenericIncomeType;
  period: string;
  amount: number;
};

export type GenericIncomeType = 'PENSION_INCOME' | 'RENTAL_INCOME' | 'MAINTENANCE_RECEIPTS' | 'ESTIMATED_GROSS_INCOME';

export type ContactDetails = {
  id: string;
  contact: string;
  contactType: ContactType;
};

export type ContactType = 'EMAIL' | 'PHONE';

export type AddressHistory = {
  id: string;
  moveInDate: Date;
  moveOutDate: Date | null;
  address: BtlAddress;
  isCurrentAddress: boolean;
};

export type BtlAddress = {
  id: string;
  addressLine1: string;
  addressLine2?: string | null;
  postcode: string;
  town: string;
  country: string;
  isManual?: boolean; // Only used in the UI
};

export type IndividualCreateDto = Omit<
  Individual,
  'id' | 'addressHistory' | 'incomes' | 'residencyStatus' | 'contactDetails'
> & {
  addressHistory: AddressHistoryCreateDto[];
  contactDetails: ContactDetailsCreateDto[];
  residencyStatus: UkResidencyCreateDto;
};

export type AddressHistoryCreateDto = Omit<AddressHistory, 'id'>;
export type ContactDetailsCreateDto = Pick<ContactDetails, 'contact' | 'contactType'>;
export type UkResidencyCreateDto = Omit<UkResidency, 'id'>;

export type CreateCompanyDto = {
  registeredAddress: Omit<BtlAddress, 'id'>;
} & Omit<BtlCompany, 'id' | 'registeredAddress'>;

export type UpdateCompanyDto = CreateCompanyDto;

export type CreateHouseholdDto = Omit<Household, 'id'> & {
  income: Omit<HouseholdIncome, 'id'>;
  declaredExpenditure: Omit<DeclaredExpenditure, 'id'>;
  creditCommitments: Omit<CreditCommitments, 'id'>;
};

export type Household = {
  id: string;
  residenceRegion: ResidenceRegion | null;
  numberOfDependents13OrUnder: number;
  numberOfDependentsOver13: number;
  income: HouseholdIncome;
  declaredExpenditure: DeclaredExpenditure;
  creditCommitments: CreditCommitments;
};

export type HouseholdsToHasIndividuals = Record<Household['id'], boolean>;

export type HouseholdIncome = {
  id: string;
  childBenefit: number;
  childTaxCredit: number;
  workingTaxCredit: number;
  industrialInjuriesDisablementBenefit: number;
  personalIndependencePayment: number;
};
export type DeclaredExpenditure = {
  id: string;
  travel: number;
  childcare: number;
  csaMaintenance: number;
  councilTax: number;
  serviceChargeGroundRent: number;
  other: number;
  basicEssentialLiving?: number | null;
  basicQualityOfLiving?: number | null;
  dependantMaintenance?: number | null;
  educationFees?: number | null;
};

export type CreditCommitments = {
  id: string;
  creditCardBalance: number;
  buyNowPayLater: number;
  unsecuredLoans: number;
  securedLoans: number;
  buyNowPayLaterOnRevertDate?: number | null;
  unsecuredLoansOnRevertDate?: number | null;
  securedLoansOnRevertDate?: number | null;
};

export enum ResidenceRegion {
  NORTH_EAST = 'NORTH_EAST',
  NORTH_WEST = 'NORTH_WEST',
  YORKSHIRE_AND_THE_HUMBER = 'YORKSHIRE_AND_THE_HUMBER',
  EAST_MIDLANDS = 'EAST_MIDLANDS',
  WEST_MIDLANDS = 'WEST_MIDLANDS',
  EAST_OF_ENGLAND = 'EAST_OF_ENGLAND',
  LONDON = 'LONDON',
  SOUTH_EAST = 'SOUTH_EAST',
  SOUTH_WEST = 'SOUTH_WEST',
}

export enum AssertionType {
  PRIVACY = 'PRIVACY',
  TERMS = 'TERMS',
  CREDIT_CONSENT = 'CREDIT_CONSENT',
  TAX_ADVICE = 'TAX_ADVICE',
  CAPITAL_GAINS_TAX_RISK = 'CAPITAL_GAINS_TAX_RISK',
  EXPAT_NON_RESIDENT = 'EXPAT_NON_RESIDENT',
  NON_CONSUMER = 'NON_CONSUMER',
  DECLARATION_COPY = 'DECLARATION_COPY',
  LET_TO_FAMILY = 'LET_TO_FAMILY',
}

export type SubmitDipDto = {
  assertions: {
    creditConsentAccepted: boolean;
    privacyAccepted: boolean;
  };
  notes?: string;
};

export type SubmitFullApplicationDto = {
  assertions: AssertionType[];
  notes?: string;
};

export type Assertor = {
  id: number;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date | null;
  email: string;
};

export type Assertion = {
  id: string;
  type: AssertionType;
  assertor: Assertor;
};

export enum SolicitorType {
  STRIDEUP = 'STRIDEUP',
  CUSTOMER = 'CUSTOMER',
}

export type CreateApplicationToSolicitorDto = {
  emailAddress: string;
  phoneNumber: string;
  addressLine1: string;
  addressLine2?: string;
  town: string;
  country: string;
  postcode: string;
  firmName: string;
  solicitorName?: string;
  solicitorType: SolicitorType;
};

export type LinkApplicationToSolicitorDto = {
  solicitorId: string;
  solicitorFirmId: string;
  solicitorType: SolicitorType;
};

export type Solicitor = {
  id: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
};

export type SolicitorFirm = {
  id: string;
  name: string;
  registeredName?: string | null;
  address: Address;
  type: SolicitorType;
  isPanelMember: boolean;
  website?: string | null;
  numberOfPartners?: number | null;
  professionalIndemnityLimit?: number | null;
  emailAddress: string;
  phoneNumber: string;
};

export type SolicitorFirmWithSolicitors = SolicitorFirm & { solicitors: Solicitor[] };

export type ApplicationToSolicitor = {
  id: string;
  isDeleted: boolean;
  solicitorType: SolicitorType;
  applicationId: string;
  solicitorFirm: SolicitorFirm;
  solicitor?: Solicitor;
};

export type UpdateBankAccountDto = {
  id?: string;
  sortCode: string;
  accountNumber: string;
  accountName: string;
  bankName: string;
  bankAddress: Address;
};

export type BankAccount = {
  id: string;
  sortCode: string;
  accountNumber: string;
  accountName: string;
  bank: Bank;
};

export type Bank = {
  id: string;
  name: string;
  address: Address;
};

export type CreateIndividualIncomesDto = {
  incomes: IncomesDto[];
  otherIncome?: OtherIncomeDto;
};

export type IncomesDto =
  | PayeIncomeDto
  | SalariedDirectorIncomeDto
  | SoleTraderIncomeDto
  | ContractorIncomeDto
  | PartnershipIncomeDto;

// TODO: need to make these different
export type UpdateIndividualIncomeDto = CreateIndividualIncomesDto;

export type IndividualIncomeDto = {
  id?: string;
  employmentType: IncomeType;
  isPrimary: boolean;
  companyName?: string;
  occupation?: string;
};

export enum IncomeType {
  PAYE_EMPLOYEE = 'PAYE_EMPLOYEE',
  SOLE_TRADER = 'SOLE_TRADER',
  CONTRACTOR = 'CONTRACTOR',
  PARTNERSHIP = 'PARTNERSHIP',
  SALARIED_DIRECTOR = 'SALARIED_DIRECTOR',
}

export type PayeIncomeDto = {
  timeInJob: number;
  basicIncome: number;
  employersAllowance: number;
  bonus: number;
  commission: number;
  overtime: number;
} & IndividualIncomeDto;

export type SalariedDirectorIncomeDto = {
  timeTrading: number;
  salaryMostRecentYear: number;
  salaryPriorYear: number;
  dividendsMostRecentYear: number;
  dividendsPriorYear: number;
} & IndividualIncomeDto;

export type SoleTraderIncomeDto = {
  timeTrading: number;
  netTaxableIncomeMostRecentYear: number;
  netTaxableIncomePriorYear: number;
} & IndividualIncomeDto;

export type ContractorIncomeDto = {
  timeTrading: number;
  timeRemainingOnContract: number;
  netTaxableIncomeMostRecentYear: number;
  netTaxableIncomePriorYear: number;
} & IndividualIncomeDto;

export type PartnershipIncomeDto = {
  timeTrading: number;
  netTaxableIncomeMostRecentYear: number;
  netTaxableIncomePriorYear: number;
} & IndividualIncomeDto;

export type OtherIncomeDto = {
  pension: number;
  rental: number;
  maintenanceReceipts: number;
};
